import {
  commitFieldValue,
  FieldWrapper,
  getFieldErrorMessage,
} from "@birdwatch/ui/form";
import type { ReactNode, RefObject } from "react";
import {
  AttachmentUploadAction,
  AttachmentUploader,
  AttachmentUploadView,
} from "@/components/ui/attachment/attachment-uploader";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";
import type { AttachmentUploadHandle } from "@/libs/attachment-upload-handle";
import type { FormAttachment } from "@/libs/form-attachment";

type AttachmentsFieldSharedProps = {
  /**
   * Shared upload handle. The SAME ref must be passed to both the body and the
   * action mount so the body reacts to uploads started from the action button.
   */
  uploadRef: RefObject<AttachmentUploadHandle | null>;
};

type AttachmentsFieldBodyProps = AttachmentsFieldSharedProps & {
  mount: "body";
  label?: ReactNode;
  description?: ReactNode;
  optional?: boolean;
  /** Width of each tile in the strip, e.g. `"w-16 shrink-0"` (default `w-24`). */
  tileClassName?: string;
  /** Class on the FieldWrapper root. */
  className?: string;
  children?: never;
  disabled?: never;
};

type AttachmentsFieldActionProps = AttachmentsFieldSharedProps & {
  mount: "action";
  disabled?: boolean;
  /** Custom trigger. Defaults to the ghost paperclip icon button. */
  children?: ReactNode;
  label?: never;
  description?: never;
  optional?: never;
  tileClassName?: never;
  className?: never;
};

export type AttachmentsFieldProps = (
  | AttachmentsFieldBodyProps
  | AttachmentsFieldActionProps
) &
  FieldBindingProps;

export function AttachmentsField(props: AttachmentsFieldProps) {
  const binding = pickBinding(props);
  return (
    <PolyField<FormAttachment[]> {...binding}>
      {(field) => {
        const value = field.state.value ?? [];
        // Adding or removing a file is a discrete commit (policy table), and it
        // is what re-runs `formAttachmentsSchema`'s pending refine → canSubmit.
        const handleChange = (next: FormAttachment[]) =>
          commitFieldValue(field, next);

        if (props.mount === "action") {
          return (
            <AttachmentUploader
              ref={props.uploadRef}
              value={value}
              onChange={handleChange}
            >
              <AttachmentUploadAction disabled={props.disabled}>
                {props.children}
              </AttachmentUploadAction>
            </AttachmentUploader>
          );
        }

        return (
          <FieldWrapper
            label={props.label}
            description={props.description}
            optional={props.optional}
            error={getFieldErrorMessage(field)}
            className={props.className}
          >
            <div className="overflow-auto">
              <AttachmentUploader
                ref={props.uploadRef}
                value={value}
                onChange={handleChange}
              >
                <AttachmentUploadView tileClassName={props.tileClassName} />
              </AttachmentUploader>
            </div>
          </FieldWrapper>
        );
      }}
    </PolyField>
  );
}
